import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

const REASON_LABELS = {
  welcome: { label: "Bienvenida", color: "text-emerald-400" },
  referral_invitee: { label: "Bono por invitación", color: "text-emerald-400" },
  referral_inviter: { label: "Bono referido compró", color: "text-emerald-400" },
  purchase_approved: { label: "Compra aprobada", color: "text-emerald-400" },
  membership_approved: { label: "Membresía", color: "text-amber-400" },
  admin_adjust: { label: "Ajuste admin", color: "text-blue-400" },
  refund_image: { label: "Reembolso imagen", color: "text-zinc-400" },
  refund_voice: { label: "Reembolso audio", color: "text-zinc-400" },
  spent_text: { label: "Gasto chat", color: "text-red-400" },
  spent_image: { label: "Gasto imagen", color: "text-red-400" },
  spent_voice: { label: "Gasto audio", color: "text-red-400" },
  spent_url_scan: { label: "Gasto escaneo URL", color: "text-red-400" },
  spent_privacy_guide: { label: "Gasto guía", color: "text-red-400" },
  spent_private_hour: { label: "Gasto modo privado", color: "text-red-400" },
  audit_baseline: { label: "Consumo histórico", color: "text-zinc-500" },
  audit_correction: { label: "Corrección auditoría", color: "text-amber-500" },
};

function ReasonBadge({ reason }) {
  const r = REASON_LABELS[reason] || { label: reason, color: "text-zinc-400" };
  return (
    <span className={`text-[10px] font-mono font-bold ${r.color}`}>
      {r.label.toUpperCase()}
    </span>
  );
}

export default function CreditAudit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [byReason, setByReason] = useState({});
  const [reason, setReason] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [auditing, setAuditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page_size: "200" });
      if (reason !== "all") params.set("reason", reason);
      if (q && /^\d+$/.test(q)) params.set("telegram_id", q);
      const r = await api.get(`/credit-ledger?${params.toString()}`);
      let filtered = r.data.items;
      if (q && !/^\d+$/.test(q)) {
        const ql = q.toLowerCase();
        filtered = filtered.filter(
          (i) =>
            (i.username || "").toLowerCase().includes(ql) ||
            (i.first_name || "").toLowerCase().includes(ql)
        );
      }
      setItems(filtered);
      setTotal(r.data.total);
      setByReason(r.data.by_reason || {});
    } catch (e) {
      toast.error("Error al cargar el ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason]);

  const runAudit = async () => {
    if (
      !window.confirm(
        "Esto recalcula y normaliza los créditos de TODOS los usuarios contra sus compras y bonos legítimos. ¿Continuar?"
      )
    )
      return;
    setAuditing(true);
    try {
      const r = await api.post("/credit-ledger/audit");
      toast.success(
        `Auditoría completada: ${r.data.users_checked} usuarios revisados, ${r.data.fix_count} corregidos.`
      );
      load();
    } catch (e) {
      toast.error("Error corriendo auditoría");
    } finally {
      setAuditing(false);
    }
  };

  // Stats
  const positive = Object.entries(byReason)
    .filter(([_, v]) => v.total_delta > 0)
    .reduce((s, [_, v]) => s + v.total_delta, 0);
  const negative = Object.entries(byReason)
    .filter(([_, v]) => v.total_delta < 0)
    .reduce((s, [_, v]) => s + v.total_delta, 0);

  const Stat = ({ label, value, color }) => (
    <div className="border border-zinc-800 bg-zinc-900/50 rounded-sm px-4 py-3">
      <div className="overline text-zinc-500">{label}</div>
      <div className={`font-display font-black text-2xl tracking-tight mt-1 ${color}`}>
        {value}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="credit-audit-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="overline mb-2">Finanzas · Trazabilidad</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Auditoría de créditos
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Cada movimiento de créditos en tu bot, con origen y motivo. Total registros:{" "}
            <span className="text-zinc-300 font-mono">{total}</span>.
          </p>
        </div>
        <Button
          onClick={runAudit}
          disabled={auditing}
          data-testid="run-audit-btn"
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm"
        >
          <ShieldCheck size={14} className={`mr-1.5 ${auditing ? "animate-spin" : ""}`} />
          {auditing ? "Auditando…" : "Correr auditoría"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Movimientos" value={total} color="text-zinc-100" />
        <Stat label="Créditos otorgados" value={`+${positive}`} color="text-emerald-400" />
        <Stat label="Créditos consumidos" value={negative} color="text-red-400" />
        <Stat
          label="Saldo neto"
          value={positive + negative}
          color="text-blue-400"
        />
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por @usuario, nombre o ID Telegram"
            data-testid="audit-search-input"
            className="bg-zinc-950 border-zinc-800 rounded-sm pl-9"
          />
        </div>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger
            className="bg-zinc-950 border-zinc-800 rounded-sm max-w-[220px]"
            data-testid="audit-reason-filter"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm max-h-80">
            <SelectItem value="all">Todos los motivos</SelectItem>
            {Object.entries(REASON_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          data-testid="audit-refresh-btn"
          className="border-zinc-800 hover:bg-zinc-900 rounded-sm"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refrescar
        </Button>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden bg-zinc-900/50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-black/40">
                <th className="text-left px-4 py-3 overline">Fecha</th>
                <th className="text-left px-4 py-3 overline">Usuario</th>
                <th className="text-right px-4 py-3 overline">Δ</th>
                <th className="text-right px-4 py-3 overline">Saldo después</th>
                <th className="text-left px-4 py-3 overline">Motivo</th>
                <th className="text-left px-4 py-3 overline">Nota</th>
                <th className="text-left px-4 py-3 overline">Origen</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-10 text-center text-zinc-500">
                    No hay movimientos con estos filtros.
                  </td>
                </tr>
              )}
              {items.map((i) => (
                <tr
                  key={i.id}
                  className="border-b border-zinc-900 hover:bg-zinc-900/40"
                  data-testid={`audit-row-${i.id}`}
                >
                  <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                    {new Date(i.created_at).toLocaleString("es-MX")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm">
                      {i.username ? `@${i.username}` : (
                        <span className="text-zinc-600">(sin user)</span>
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-600 font-mono">
                      {i.telegram_id}
                    </div>
                  </td>
                  <td
                    className={`px-4 py-3 text-right font-mono font-bold whitespace-nowrap ${
                      i.delta >= 0 ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {i.delta >= 0 ? `+${i.delta}` : i.delta}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-300">
                    {i.balance_after}
                  </td>
                  <td className="px-4 py-3">
                    <ReasonBadge reason={i.reason} />
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400 max-w-[400px]">
                    {i.note || "—"}
                  </td>
                  <td className="px-4 py-3 text-[10px] font-mono text-zinc-500">
                    {i.actor}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
